import { apiClient } from './client';

export type InvoiceStatus = 'PENDING' | 'PAID' | 'PARTIALLY_PAID' | 'CANCELLED';

export interface SubscriptionConfig {
  id: number;
  revenuePercentage: number;
  ceilingAmount?: number;
  gstRate: number;
  newUserDiscountPct?: number;
  newUserDiscountMonths?: number;
  updatedAt: string;
}

export interface SubscriptionInvoice {
  id: number;
  invoiceNumber: string;
  wholesalerTenantId: number;
  // Enriched fields — present on admin list endpoint, absent on wholesaler endpoint
  wholesalerName?: string;
  city?: string;
  state?: string;
  industry?: string;
  vendorName?: string;
  softwareVendorId?: number;
  periodYear: number;
  periodMonth: number;
  totalRevenue: number;
  invoiceAmount: number;
  discountPct?: number;
  discountAmt?: number;
  netPayable?: number;
  revenuePercentage?: number;
  gstAmount?: number;
  totalAmount?: number;
  paidAmount: number;
  dueAmount: number;
  status: InvoiceStatus;
  extensionDays?: number;
  razorpayOrderId?: string;
  createdAt: string;
  updatedAt?: string;
}

export interface PageResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

// Admin endpoints
export async function getConfig(): Promise<SubscriptionConfig> {
  const resp = await apiClient.get('/admin/subscription/config');
  return resp.data;
}

/** Any authenticated tenant — returns gstRate for display purposes. */
export async function getMyConfig(): Promise<{ gstRate: number }> {
  const resp = await apiClient.get('/admin/subscription/my-config');
  return resp.data;
}

export async function updateConfig(data: {
  revenuePercentage: number;
  ceilingAmount?: number | null;
  gstRate?: number | null;
  newUserDiscountPct?: number | null;
  newUserDiscountMonths?: number | null;
}): Promise<SubscriptionConfig> {
  const resp = await apiClient.put('/admin/subscription/config', data);
  return resp.data;
}

export interface InvoiceTotals {
  totalFee: number;
  totalPaid: number;
  totalDue: number;
}

/** Scopes the invoice list's tenant text search to a single field. */
export type InvoiceSearchField = 'name' | 'gst' | 'phone' | 'code';

type InvoiceFilterParams = {
  status?: InvoiceStatus;
  q?: string;
  searchField?: InvoiceSearchField;
  industry?: string;
  vendorName?: string;
  softwareVendorId?: number;
  noGstin?: boolean;
  noLicense?: boolean;
  licenseExpired?: boolean;
  periodYear?: number;
  periodMonth?: number;
};

export async function listAllInvoices(params?: InvoiceFilterParams & {
  page?: number;
  size?: number;
}): Promise<PageResponse<SubscriptionInvoice>> {
  const resp = await apiClient.get('/admin/subscription/invoices', { params });
  return resp.data;
}

export async function fetchInvoiceTotals(params?: InvoiceFilterParams): Promise<InvoiceTotals> {
  const resp = await apiClient.get('/admin/subscription/invoices/totals', { params });
  return resp.data;
}

export async function generateInvoices(year: number, month: number): Promise<SubscriptionInvoice[]> {
  const resp = await apiClient.post('/admin/subscription/invoices/generate', { year, month });
  return resp.data;
}

/** Upsert invoices for the current month up to today — creates missing rows and refreshes PENDING ones. */
export async function calculateInvoices(year: number, month: number): Promise<SubscriptionInvoice[]> {
  const resp = await apiClient.post('/admin/subscription/invoices/calculate', { year, month });
  return resp.data;
}

/** Admin: manually mark an invoice as paid (e.g. offline / cash payment). */
export async function markInvoicePaid(id: number, amount: number): Promise<SubscriptionInvoice> {
  const resp = await apiClient.post(`/admin/subscription/invoices/${id}/mark-paid`, { amount });
  return resp.data;
}

/** Super-admin only: grant extension days on an invoice (delays the 20-day block). */
export async function extendInvoice(id: number, extensionDays: number): Promise<SubscriptionInvoice> {
  const resp = await apiClient.post(`/admin/subscription/invoices/${id}/extend`, { extensionDays });
  return resp.data;
}

/** Admin: all invoices (all periods) for a specific supplier, enriched with tenant details. */
export async function getSupplierInvoices(wholesalerId: number): Promise<SubscriptionInvoice[]> {
  const resp = await apiClient.get(`/admin/subscription/invoices/supplier/${wholesalerId}`);
  return resp.data;
}

export interface DailyRevenueRow {
  date: string;       // ISO yyyy-MM-dd
  revenue: number;
  orderCount: number;
}

/** Admin: daily revenue breakdown for a supplier in a given month. */
export async function getDailyRevenue(wholesalerId: number, year: number, month: number): Promise<DailyRevenueRow[]> {
  const resp = await apiClient.get(`/admin/subscription/invoices/${wholesalerId}/daily-revenue`, {
    params: { year, month },
  });
  return resp.data;
}

/** Wholesaler: daily revenue for their own period (self-service). */
export async function myDailyRevenue(year: number, month: number): Promise<DailyRevenueRow[]> {
  const resp = await apiClient.get('/admin/subscription/my-daily-revenue', { params: { year, month } });
  return resp.data;
}

// Wholesaler endpoint
export async function myInvoices(params?: {
  status?: InvoiceStatus;
  page?: number;
  size?: number;
}): Promise<PageResponse<SubscriptionInvoice>> {
  const resp = await apiClient.get('/admin/subscription/my-invoices', { params });
  return resp.data;
}

export async function initiatePayment(invoiceId: number): Promise<{
  razorpayOrderId: string;
  amount: string;
  currency: string;
  invoiceNumber: string;
}> {
  const resp = await apiClient.post(`/admin/subscription/invoices/${invoiceId}/payment/initiate`);
  return resp.data;
}

